import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from "firebase/auth";
import { auth } from "../../../../../config/firebaseConfig";
import { Button, TextInput, PasswordStrengthIndicator } from "../../../../../components";
import { validateEmail } from "../../../../../assets/js/validation";
import ParentStudentSwitch from "./ParentStudentSwitch";
import { toast } from "react-toastify";

const EmailRegisterForm = ({ onBack, onSwitchToLogin, onVerificationSent }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [registerAs, setRegisterAs] = useState("parent");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetFieldError = (field) => {
    setError((prev) => (prev ? { ...prev, [field]: null } : null));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const cleanEmail = email.trim().toLowerCase();
    const cleanName = name.trim();
    const errors = {};

    if (!cleanName) {
      errors.name = "Full name is required.";
    } else if (cleanName.length < 2) {
      errors.name = "Please enter a valid full name.";
    }

    if (!cleanEmail) {
      errors.email = "Email is required.";
    } else if (!validateEmail(cleanEmail)) {
      errors.email = "Please enter a valid email address.";
    }

    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Confirm password is required.";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, password);
      const user = userCredential.user;

      if (cleanName) {
        await updateProfile(user, { displayName: cleanName });
      }

      await sendEmailVerification(user);
      toast.success("Verification link sent to your email!");

      if (onVerificationSent) {
        onVerificationSent({
          email: cleanEmail,
          name: cleanName,
          registerAs,
          password,
        });
      }
    } catch (err) {
      console.error("Signup error:", err);
      if (err.code === "auth/email-already-in-use") {
        setError({
          email: "This email address is already registered. Please sign in or use a different email.",
        });
      } else if (err.code === "auth/weak-password") {
        setError({ password: "Password is too weak. Please use at least 8 characters." });
      } else if (err.code === "auth/invalid-email") {
        setError({ email: "Invalid email format." });
      } else {
        setError({ other: err.message || "Failed to create account. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/register" + (location.search || ""));
    }
  };

  const handleLoginClick = () => {
    if (onSwitchToLogin) {
      onSwitchToLogin();
    } else {
      navigate("/login/email" + (location.search || ""));
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="w-full h-full flex flex-col justify-start" noValidate>
      {/* Back Button */}
      <button
        type="button"
        onClick={handleBackClick}
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-800 hover:text-black mb-2 transition-colors w-fit focus:outline-none cursor-pointer"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        <span>Back</span>
      </button>

      {/* Heading & Subheading */}
      <h3 className="text-xl sm:text-2xl font-extrabold text-[#111827] tracking-tight leading-tight mb-0.5">
        Create Your Account
      </h3>
      <p className="text-xs text-gray-500 font-medium mb-3">
        Sign up to continue your learning journey
      </p>

      {/* TODO: Re-activate role switcher in future releases
      <ParentStudentSwitch
        registerAs={registerAs}
        setRegisterAs={setRegisterAs}
        labels={{ parent: "I'm Parent", student: "I'm Student" }}
      />
      */}

      {/* Full Name Input */}
      <div className="form-group flex flex-col mb-2.5">
        <label htmlFor="name" className="text-xs sm:text-sm font-bold text-gray-700 mb-1">
          Full Name <span className="text-[#E5383B]">*</span>
        </label>
        <TextInput
          type="text"
          placeholderText="Alex Johnson"
          inputName="name"
          inputValue={name}
          isRequired={true}
          inputClassName="!h-[44px] !rounded-xl !bg-white !border-gray-200 focus:!border-[#6366F1] !text-sm"
          leftIcon={
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          }
          handleChange={(e) => {
            setName(e.target.value);
            resetFieldError("name");
          }}
        />
        {error?.name && <div className="text-xs text-[#f00] mt-1 pl-1">{error.name}</div>}
      </div>

      {/* Email Input */}
      <div className="form-group flex flex-col mb-2.5">
        <label htmlFor="email" className="text-xs sm:text-sm font-bold text-gray-700 mb-1">
          Email Address <span className="text-[#E5383B]">*</span>
        </label>
        <TextInput
          type="email"
          placeholderText="alex@example.com"
          inputName="email"
          inputValue={email}
          isRequired={true}
          inputClassName="!h-[44px] !rounded-xl !bg-white !border-gray-200 focus:!border-[#6366F1] !text-sm"
          leftIcon={
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          }
          handleChange={(e) => {
            setEmail(e.target.value);
            resetFieldError("email");
          }}
        />
        {error?.email && <div className="text-xs text-[#f00] mt-1 pl-1">{error.email}</div>}
      </div>

      {/* Password Input */}
      <div className="form-group flex flex-col mb-1.5">
        <label htmlFor="password" className="text-xs sm:text-sm font-bold text-gray-700 mb-1">
          Create Password <span className="text-[#E5383B]">*</span>
        </label>
        <TextInput
          type="password"
          placeholderText="••••••••"
          inputName="password"
          inputValue={password}
          isRequired={true}
          inputClassName="!h-[44px] !rounded-xl !bg-white !border-gray-200 focus:!border-[#6366F1] !text-sm"
          leftIcon={
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          }
          handleChange={(e) => {
            setPassword(e.target.value);
            resetFieldError("password");
          }}
        />
        {error?.password && <div className="text-xs text-[#f00] mt-1 pl-1">{error.password}</div>}
      </div>

      {/* Dynamic Password Strength Indicator */}
      <div className="mb-2">
        <PasswordStrengthIndicator password={password} />
      </div>

      {/* Confirm Password Input */}
      <div className="form-group flex flex-col mb-2.5">
        <label htmlFor="confirmPassword" className="text-xs sm:text-sm font-bold text-gray-700 mb-1">
          Confirm Password <span className="text-[#E5383B]">*</span>
        </label>
        <TextInput
          type="password"
          placeholderText="••••••••"
          inputName="confirmPassword"
          inputValue={confirmPassword}
          isRequired={true}
          inputClassName="!h-[44px] !rounded-xl !bg-white !border-gray-200 focus:!border-[#6366F1] !text-sm"
          leftIcon={
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          }
          handleChange={(e) => {
            setConfirmPassword(e.target.value);
            resetFieldError("confirmPassword");
          }}
        />
        {error?.confirmPassword && (
          <div className="text-xs text-[#f00] mt-1 pl-1">{error.confirmPassword}</div>
        )}
      </div>

      {error?.other && (
        <div className="text-[#E5383B] text-xs font-semibold mb-2.5">{error.other}</div>
      )}

      {/* Submit Button */}
      <div className="w-full mt-1.5">
        <Button
          version="secondary"
          text="Continue"
          children={
            <svg className="w-4 h-4 ml-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          }
          type="submit"
          loading={loading}
          width="100%"
          className="w-full justify-center font-extrabold"
        />
      </div>

      {/* Footer Link */}
      <div className="text-center mt-3">
        <p className="text-xs text-gray-500 font-medium">
          Already have an account?{" "}
          <button
            type="button"
            onClick={handleLoginClick}
            className="text-[#009DFF] hover:underline font-bold focus:outline-none cursor-pointer"
          >
            Sign in here
          </button>
        </p>
      </div>
    </form>
  );
};

export default EmailRegisterForm;
